import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, severityStyle, timeAgo } from "@/components/shared";
import {
  Bell,
  ShieldCheck,
  AlertTriangle,
  Info,
  Skull,
  Loader2,
} from "lucide-react";

const ICON = {
  success: ShieldCheck,
  warning: AlertTriangle,
  critical: Skull,
  info: Info,
};

const FILTERS = ["all", "critical", "warning", "success", "info"];

export default function Alerts() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/alerts").then((r) => setItems(r.data));
  }, []);

  if (!items)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );

  const filtered = filter === "all" ? items : items.filter((i) => i.severity === filter);
  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === "all" ? items.length : items.filter((i) => i.severity === f).length;
    return acc;
  }, {});

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <PageHeader
        tag="Event Feed"
        title="Activity & Alerts"
        subtitle="A chronological log of every scan, block and security event across your defense grid."
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 border font-mono text-xs uppercase tracking-widest transition-colors duration-200 ${
              filter === f ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"
            }`}
          >
            {f} ({counts[f]})
          </button>
        ))}
      </div>

      <div className="bg-card border border-border">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Bell size={28} strokeWidth={1.5} className="mx-auto mb-3 opacity-40" />
            <p className="font-mono text-sm">No events in this category.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((it, idx) => {
              const Icon = ICON[it.severity] || Info;
              return (
                <div
                  key={it.id}
                  data-testid={`alert-${idx}`}
                  className="flex items-start gap-4 px-6 py-4 hover:bg-primary/[0.03] transition-colors animate-fade-up"
                  style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                >
                  <div className={`w-9 h-9 shrink-0 flex items-center justify-center border ${severityStyle(it.severity)}`}>
                    <Icon size={16} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-sm text-foreground">{it.event}</span>
                      <span className={`px-1.5 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${severityStyle(it.severity)}`}>
                        {it.severity}
                      </span>
                    </div>
                    {it.detail && <div className="text-xs text-muted-foreground mt-1 truncate">{it.detail}</div>}
                  </div>
                  <span className="font-mono text-xs text-muted-foreground shrink-0">{timeAgo(it.timestamp)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
